/**
 * Microphone toggle button for voice mode.
 *
 * Placed in PromptInput alongside Send/Stop buttons.
 * Requests browser mic permission on first click.
 */

import React from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { useVoiceOptional } from '../../context/VoiceContext';

interface VoiceButtonProps {
  /** Active conversation id. Empty string when no conversation is loaded. */
  sessionId: string;
  userId: string;
  disabled?: boolean;
  /**
   * Spec 052 Step 9: lazy session bootstrap for voice-first flow.
   * Called when the user clicks Start while no conversation is yet
   * loaded (`sessionId === ''`). Should create a conversation and
   * return its id. The voice WS will open against the returned id.
   */
  onCreateSession?: () => Promise<string | null>;
}

export function VoiceButton({
  sessionId,
  userId,
  disabled,
  onCreateSession,
}: VoiceButtonProps) {
  const voice = useVoiceOptional();

  if (!voice) return null;

  const { voiceState, startVoice, stopVoice } = voice;
  const isActive = voiceState === 'active';
  const isConnecting = voiceState === 'connecting';
  const isError = voiceState === 'error';

  const handleClick = async () => {
    if (isActive || isConnecting) {
      stopVoice();
      return;
    }
    let effectiveSessionId = sessionId;
    if (!effectiveSessionId && onCreateSession) {
      const created = await onCreateSession();
      if (!created) return;
      effectiveSessionId = created;
    }
    if (!effectiveSessionId) return;
    await startVoice(effectiveSessionId, userId);
  };

  const title = isActive
    ? 'Stop voice mode'
    : isConnecting
      ? 'Connecting...'
      : isError
        ? 'Voice error - click to retry'
        : 'Start voice mode';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled && !isActive}
      className={`flex-shrink-0 p-2.5 rounded-xl transition-all duration-200 ${
        isActive
          ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600 shadow-md hover:shadow-lg active:scale-95'
          : isConnecting
            ? 'bg-sky-100 text-sky-600 cursor-wait'
            : isError
              ? 'bg-red-50 text-red-500 hover:bg-red-100'
              : disabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-500 hover:text-sky-700 hover:bg-sky-50'
      }`}
      title={title}
      data-testid="voice-button"
    >
      {isConnecting ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : isActive ? (
        <MicOff className="w-5 h-5" />
      ) : (
        <Mic className="w-5 h-5" />
      )}
    </button>
  );
}
